// Types and Data for Excellence Index survey
// Measures commitment to excellence across multiple dimensions

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'
import { SurveyQuestionTypes } from './SelfKnowledgeSurvey.ts'

export type ExcellenceSurveyId = string

export interface ExcellenceSurvey {
    id: ExcellenceSurveyId
    surveyType: SurveyTypes.EXCELLENCE
    userId: UserId
    hash?: string
    questions: ExcellenceQuestionType[]
    scenarios?: ExcellenceScenario[]
    allItems?: ExcellenceSurveyItem[] // Questions + scenarios shuffled for presentation
    scores: ExcellenceScores
    overallScore: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Five dimensions of excellence
// Note: ADAPTABILITY replaced GROWTH_MINDSET to avoid overlap with GrowthMindsetSurvey
// Adaptability focuses on flexibility and openness to change in context of excellence
export enum ExcellenceDimension {
    ADAPTABILITY = 'adaptability', // Adaptabilita (flexibilita, otvorenosť zmenám)
    PERSEVERANCE = 'perseverance', // Vytrvalosť
    LEARNING_AGILITY = 'learning_agility', // Schopnosť učiť sa
    QUALITY_STANDARDS = 'quality_standards', // Štandardy kvality
    CONTINUOUS_IMPROVEMENT = 'continuous_improvement' // Neustále zlepšovanie
}

export const ExcellenceDimensionLabels: Record<ExcellenceDimension, string> = {
    [ExcellenceDimension.ADAPTABILITY]: 'Adaptabilita',
    [ExcellenceDimension.PERSEVERANCE]: 'Vytrvalosť',
    [ExcellenceDimension.LEARNING_AGILITY]: 'Schopnosť učiť sa',
    [ExcellenceDimension.QUALITY_STANDARDS]: 'Štandardy kvality',
    [ExcellenceDimension.CONTINUOUS_IMPROVEMENT]: 'Neustále zlepšovanie'
}

export const ExcellenceDimensionDescriptions: Record<ExcellenceDimension, string> = {
    [ExcellenceDimension.ADAPTABILITY]: 'Flexibilita pri zmenách a otvorenosť novým prístupom',
    [ExcellenceDimension.PERSEVERANCE]: 'Vytrvalosť pri prekonávaní prekážok a dosahovaniu cieľov',
    [ExcellenceDimension.LEARNING_AGILITY]: 'Rýchlosť a efektivita pri osvojovaní nových znalostí',
    [ExcellenceDimension.QUALITY_STANDARDS]: 'Orientácia na vysokú kvalitu a detaily',
    [ExcellenceDimension.CONTINUOUS_IMPROVEMENT]: 'Neustále hľadanie možností na zlepšenie'
}

export interface ExcellenceQuestionType {
    id: string
    title: string
    dimension: ExcellenceDimension
    type: SurveyQuestionTypes
    reversed: boolean // Some questions are reverse-scored
    value?: number // User's answer (1-5)
}

export interface ExcellenceScores {
    [ExcellenceDimension.ADAPTABILITY]: number
    [ExcellenceDimension.PERSEVERANCE]: number
    [ExcellenceDimension.LEARNING_AGILITY]: number
    [ExcellenceDimension.QUALITY_STANDARDS]: number
    [ExcellenceDimension.CONTINUOUS_IMPROVEMENT]: number
}

// Behavioral scenario types - reveal actual behavior vs stated beliefs
export interface ExcellenceScenarioOption {
    id: string
    text: string
    level: 'low' | 'medium' | 'high' // Excellence orientation level
}

export interface ExcellenceScenario {
    id: string
    dimension: ExcellenceDimension
    type: 'scenario'
    scenario: string
    question: string
    options: ExcellenceScenarioOption[]
    selectedOptionId?: string
}

export type ExcellenceSurveyItem = ExcellenceQuestionType | ExcellenceScenario

export function isExcellenceScenario(item: ExcellenceSurveyItem): item is ExcellenceScenario {
    return 'type' in item && item.type === 'scenario'
}

// 30 questions - 6 per dimension
// Scale: 1 = Vôbec nesúhlasím, 5 = Úplne súhlasím
export const EXCELLENCE_QUESTIONS: ExcellenceQuestionType[] = [
    // ADAPTABILITY (Adaptabilita) - 6 questions - focuses on flexibility and openness to change
    {
        id: uuidv4(),
        title: 'Dokážem sa rýchlo prispôsobiť novým situáciám a požiadavkám',
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Zmeny vnímam ako príležitosť, nie ako ohrozenie',
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Keď sa zmenia podmienky, mám problém sa prispôsobiť',
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Som otvorený/otvorená novým prístupom, aj keď sú odlišné od mojich',
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'V neistých situáciách dokážem nájsť cestu vpred',
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Preferujem stabilitu a predvídateľnosť pred zmenami',
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },

    // PERSEVERANCE (Vytrvalosť) - 6 questions
    {
        id: uuidv4(),
        title: 'Dokončujem, čo som začal/začala, aj keď je to náročné',
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Prekážky ma neodrádzajú od dosahovania cieľov',
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Často sa vzdávam projektov skôr, než ich dokončím',
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Pracujem na dlhodobých cieľoch aj keď výsledky nie sú okamžité',
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Som ochotný/ochotná investovať extra úsilie na dosiahnutie dokonalosti',
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Keď narazím na ťažkosti, moja motivácia rýchlo klesá',
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },

    // LEARNING AGILITY (Schopnosť učiť sa) - 6 questions
    {
        id: uuidv4(),
        title: 'Rýchlo sa učím nové veci',
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Dokážem aplikovať poznatky z jednej oblasti do inej',
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Potrebujem veľa času na pochopenie nových konceptov',
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Aktívne vyhľadávam príležitosti na vzdelávanie',
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Experimentujem s rôznymi prístupmi k riešeniu problémov',
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Preferujem overené metódy pred učením sa nových',
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },

    // QUALITY STANDARDS (Štandardy kvality) - 6 questions
    {
        id: uuidv4(),
        title: 'Dbám na detaily vo svojej práci',
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Mám vysoké štandardy pre kvalitu mojej práce',
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: '"Dosť dobré" je pre mňa dostatočné',
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Pravidelne kontrolujem svoju prácu na chyby',
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Snažím sa podávať výkony na najvyššej úrovni',
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Malé nedokonalosti ma netrápia',
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },

    // CONTINUOUS IMPROVEMENT (Neustále zlepšovanie) - 6 questions
    {
        id: uuidv4(),
        title: 'Pravidelne premýšľam o tom, ako môžem byť lepší/lepšia',
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Hľadám spôsoby, ako zefektívniť svoju prácu',
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Som spokojný/spokojná s tým, ako veci robím teraz',
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    },
    {
        id: uuidv4(),
        title: 'Učím sa zo svojich chýb a používam ich na zlepšenie',
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Porovnávam sa s najlepšími, aby som sa zlepšil/zlepšila',
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: false
    },
    {
        id: uuidv4(),
        title: 'Nemám potrebu neustále sa zlepšovať',
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: SurveyQuestionTypes.SCALE,
        reversed: true
    }
]

// 5 Behavioral scenarios - one per dimension
// Reveal actual behavior vs stated beliefs (weighted 1.5x in scoring)
export const EXCELLENCE_SCENARIOS: ExcellenceScenario[] = [
    // ADAPTABILITY scenario
    {
        id: uuidv4(),
        dimension: ExcellenceDimension.ADAPTABILITY,
        type: 'scenario',
        scenario:
            'Tvoja firma oznámila reorganizáciu. Tvoj tím sa zlúči s iným oddelením, dostaneš nového šéfa a budeš pracovať na úplne inom projekte. Máš dva týždne na prechod. Niektorí kolegovia sú nahnevaní a sťažujú sa.',
        question: 'Ako zareaguješ?',
        options: [
            {
                id: uuidv4(),
                text: 'Som frustrovaný/á. Fungovali sme dobre a toto všetko zbytočne naruší. Budem sa sťažovať HR a manažmentu.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Počkám a uvidím. Nemá zmysel sa stresovať skôr, než zistím, ako to bude v praxi vyzerať.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Zorganizujem stretnutie s novým tímom, aby sme sa spoznali. Zmena môže byť príležitosťou naučiť sa niečo nové.',
                level: 'high'
            }
        ]
    },
    // PERSEVERANCE scenario
    {
        id: uuidv4(),
        dimension: ExcellenceDimension.PERSEVERANCE,
        type: 'scenario',
        scenario:
            'Už tri mesiace pracuješ na náročnom projekte. Výsledky zatiaľ nie sú viditeľné, termín sa blíži a objavili sa nové komplikácie. Kolegovia, ktorí začínali s tebou, už projekt opustili alebo presunuli prioritu inam.',
        question: 'Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Tri mesiace bez výsledkov je dosť. Navrhnem projekt pozastaviť a presunúť sa na niečo s rýchlejším výstupom.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Porozprávam sa so šéfom o predĺžení termínu. Ak to nebude možné, budem hľadať kompromis.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Preskúmam komplikácie, upravím plán a budem pokračovať. Tri mesiace práce nevyhodím. Výsledky prídu, ak vytrvám.',
                level: 'high'
            }
        ]
    },
    // LEARNING AGILITY scenario
    {
        id: uuidv4(),
        dimension: ExcellenceDimension.LEARNING_AGILITY,
        type: 'scenario',
        scenario:
            'Na konferencii si počul/a o novej technológii, ktorá by mohla výrazne zlepšiť tvoju prácu. Nikto vo firme ju ešte nepoužíva, dokumentácia je len v angličtine a vyžaduje to naučiť sa úplne nový koncept.',
        question: 'Ako sa k tomu postavíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Počkám, kým to niekto otestuje a pripraví v slovenčine. Nemám čas učiť sa niečo, čo možno ani nebude fungovať.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Prečítam si základné články a pozriem pár videí. Ak to bude vyzerať sľubne, možno to vyskúšam na menšom projekte.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Hneď začnem študovať dokumentáciu. Vytvorím si malý experiment, aby som pochopil/a princípy. Nové veci sa učím rád/rada.',
                level: 'high'
            }
        ]
    },
    // QUALITY STANDARDS scenario
    {
        id: uuidv4(),
        dimension: ExcellenceDimension.QUALITY_STANDARDS,
        type: 'scenario',
        scenario:
            'Dokončil/a si report pre klienta. Máš hodinu do deadline. Pri poslednej kontrole si si všimol/a malú chybu v grafe a niekoľko preklépov v texte. Obsahovo je report správny a klient pravdepodobne tieto chyby ani nezaregistruje.',
        question: 'Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Odošlem ho tak. Obsahovo je správny a nemám čas na kozmetické úpravy. Klient si to nevšimne.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Opravím najviditeľnejšie chyby a zvyšok nechám. Dokonalé nepriateľ dobrého.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Opravím všetko. Môj report reprezentuje moju prácu. Aj malé chyby znižujú dôveryhodnosť celého dokumentu.',
                level: 'high'
            }
        ]
    },
    // CONTINUOUS IMPROVEMENT scenario
    {
        id: uuidv4(),
        dimension: ExcellenceDimension.CONTINUOUS_IMPROVEMENT,
        type: 'scenario',
        scenario:
            'Už rok robíš tú istú úlohu rovnakým spôsobom. Funguje to, máš to za 30 minút a výsledky sú akceptovateľné. Kolega ti ukázal nástroj, ktorý by to mohol zautomatizovať, ale naučiť sa ho by trvalo dva dni.',
        question: 'Ako sa rozhodneš?',
        options: [
            {
                id: uuidv4(),
                text: 'Nechám to tak. Funguje to, prečo meniť niečo, čo nie je pokazené? Dva dni je veľa času.',
                level: 'low'
            },
            {
                id: uuidv4(),
                text: 'Keď budem mať voľný čas, pozriem sa na ten nástroj. Zatiaľ budem pokračovať po starom.',
                level: 'medium'
            },
            {
                id: uuidv4(),
                text: 'Investujem tie dva dni. Ak to robím rok, automatizácia mi ušetrí stovky hodín. Vždy hľadám spôsoby, ako byť efektívnejší.',
                level: 'high'
            }
        ]
    }
]

// Fisher-Yates shuffle for randomizing items
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// Initialize a new Excellence survey
export function initializedExcellenceSurvey(userId: UserId): Omit<ExcellenceSurvey, 'id' | 'createdAt'> {
    const questions = EXCELLENCE_QUESTIONS.map((q) => ({ ...q, value: undefined }))
    const scenarios = EXCELLENCE_SCENARIOS.map((s) => ({ ...s, selectedOptionId: undefined }))

    // Combine and shuffle questions + scenarios for varied presentation
    const allItems: ExcellenceSurveyItem[] = shuffleArray([...questions, ...scenarios])

    return {
        surveyType: SurveyTypes.EXCELLENCE,
        userId,
        hash: uuidv4(),
        questions,
        scenarios,
        allItems,
        scores: {
            [ExcellenceDimension.ADAPTABILITY]: 0,
            [ExcellenceDimension.PERSEVERANCE]: 0,
            [ExcellenceDimension.LEARNING_AGILITY]: 0,
            [ExcellenceDimension.QUALITY_STANDARDS]: 0,
            [ExcellenceDimension.CONTINUOUS_IMPROVEMENT]: 0
        },
        overallScore: 0,
        isDraft: true,
        deletedAt: null
    }
}
